import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

export function useSortableTable<T>(
  data: T[],
  initialSort?: { field: string; direction: SortDirection },
  // Lets a column sort by something other than its own raw field — e.g. a
  // "Size" column that reads weight_kg for air shipments but total_cbm for
  // everything else has no single raw field to sort by. Falls back to plain
  // field access when a column has no accessor registered.
  accessors?: Record<string, (item: T) => unknown>
) {
  const [sortConfig, setSortConfig] = useState<SortConfig>(
    initialSort || { field: '', direction: null }
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.field || !sortConfig.direction) return data;
    const accessor = accessors?.[sortConfig.field];

    return [...data].sort((a, b) => {
      const aValue = accessor ? accessor(a) : (a as any)[sortConfig.field];
      const bValue = accessor ? accessor(b) : (b as any)[sortConfig.field];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig, accessors]);

  const requestSort = (field: string) => {
    setSortConfig(prev => {
      if (prev.field !== field) return { field, direction: 'asc' };
      if (prev.direction === 'asc') return { field, direction: 'desc' };
      return { field: '', direction: null };
    });
  };

  const resetSort = () => {
    setSortConfig({ field: '', direction: null });
  };

  return { sortedData, sortConfig, requestSort, resetSort };
}
